import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Copy,
  Check,
  Download,
  MessageCircle,
  Twitter,
  Facebook,
  Send,
  Loader2,
  Mail,
  Linkedin,
  Share2,
  Trophy,
  BadgeCheck,
  Sparkles,
  Star,
  Handshake,
} from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import { API } from "@/App";

/**
 * ShareDealDialog
 * Celebratory share card when a deal is completed between Brand and Star.
 * Shows both profile pics, star name with Pro Creator badge, and celebration graphics.
 */
export default function ShareDealDialog({ open, onClose, dealData }) {
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [brandImgFailed, setBrandImgFailed] = useState(false);
  const [starImgFailed, setStarImgFailed] = useState(false);
  const cardRef = useRef(null);

  if (!dealData) return null;

  const { brand, star, dealAmount } = dealData;

  // Build share URLs
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const starProfileUrl = `${origin}/star/${star?.user_id}`;
  const shareMessage = `🎉 Deal Completed! ${star?.name} just collaborated with ${brand?.name || brand?.company_name || "a Brand"} on FOUNDSTAR! ✨`;
  const shareCombined = `${shareMessage}\n\nCheck out their profile: ${starProfileUrl}`;

  const enc = encodeURIComponent;
  const socials = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      url: `https://wa.me/?text=${enc(shareCombined)}`,
      bg: "bg-[#25D366] hover:bg-[#1EBE5A]",
      Icon: MessageCircle,
    },
    {
      key: "telegram",
      label: "Telegram",
      url: `https://t.me/share/url?url=${enc(starProfileUrl)}&text=${enc(shareMessage)}`,
      bg: "bg-[#26A5E4] hover:bg-[#1E90C9]",
      Icon: Send,
    },
    {
      key: "twitter",
      label: "Twitter / X",
      url: `https://twitter.com/intent/tweet?text=${enc(shareMessage)}&url=${enc(starProfileUrl)}`,
      bg: "bg-black hover:bg-neutral-800",
      Icon: Twitter,
    },
    {
      key: "facebook",
      label: "Facebook",
      url: `https://www.facebook.com/sharer/sharer.php?u=${enc(starProfileUrl)}&quote=${enc(shareMessage)}`,
      bg: "bg-[#1877F2] hover:bg-[#1565D8]",
      Icon: Facebook,
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(starProfileUrl)}`,
      bg: "bg-[#0A66C2] hover:bg-[#085196]",
      Icon: Linkedin,
    },
    {
      key: "email",
      label: "Email",
      url: `mailto:?subject=${enc(`Deal Completed on FOUNDSTAR!`)}&body=${enc(shareCombined)}`,
      bg: "bg-[#64748B] hover:bg-[#475569]",
      Icon: Mail,
    },
  ];

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareCombined);
      setCopied(true);
      toast.success("Message copied!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: null,
        scale: 3,
        useCORS: true,
        allowTaint: true,
      });
      const link = document.createElement("a");
      link.download = `deal-completed-${(star?.name || "star").replace(/\s+/g, "_")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Deal card downloaded!");
    } catch (e) {
      console.error(e);
      toast.error("Download failed. Try again.");
    } finally {
      setDownloading(false);
    }
  };

  const shareNative = async () => {
    const shareData = {
      title: "Deal Completed on FOUNDSTAR!",
      text: shareMessage,
      url: starProfileUrl,
    };
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share(shareData);
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return;
      }
    }
    copyLink();
  };

  // Resolve photo URLs - use proxy for CORS
  const resolvePhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith("http")) {
      // External URLs need proxy for CORS in html2canvas
      return photo;
    }
    if (photo.startsWith("/api/")) {
      return `${API.replace("/api", "")}${photo}`;
    }
    return `${API}/files/${photo}`;
  };

  // Get base URL without /api for file serving
  const baseUrl = API.replace("/api", "");
  
  const brandPhotoUrl = resolvePhotoUrl(brand?.picture || brand?.logo || brand?.profile_photo);
  const starPhotoUrl = resolvePhotoUrl(star?.picture || star?.photo_url || star?.profile_photo);

  const showBrandImg = brandPhotoUrl && !brandImgFailed;
  const showStarImg = starPhotoUrl && !starImgFailed;

  const formatAmount = (amt) => {
    if (!amt) return null;
    if (amt >= 100000) return `₹${(amt / 100000).toFixed(1)}L`;
    if (amt >= 1000) return `₹${(amt / 1000).toFixed(0)}K`;
    return `₹${amt}`;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#1E293B]">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Deal Completed! 🎉
          </DialogTitle>
        </DialogHeader>

        {/* Preview Card for Download */}
        <div
          ref={cardRef}
          className="relative rounded-2xl overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #1E293B 0%, #3B1361 50%, #1E293B 100%)",
          }}
        >
          {/* Confetti/Sparkle decorations */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            {[...Array(12)].map((_, i) => (
              <Sparkles
                key={i}
                className="absolute text-yellow-300/40"
                style={{
                  top: `${Math.random() * 100}%`,
                  left: `${Math.random() * 100}%`,
                  width: 12 + Math.random() * 8,
                  height: 12 + Math.random() * 8,
                  transform: `rotate(${Math.random() * 360}deg)`,
                }}
              />
            ))}
          </div>

          <div className="relative z-10 p-6">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-yellow-400 to-orange-400 text-white px-4 py-2 rounded-full text-sm font-bold mb-3">
                <Trophy className="w-4 h-4" />
                DEAL COMPLETED
              </div>
              <h2 className="text-white font-outfit font-bold text-xl">Collaboration Success!</h2>
            </div>

            {/* Brand & Star Connection */}
            <div className="flex items-center justify-center gap-6 mb-6">
              {/* Brand Photo */}
              <div className="relative">
                <div className="w-24 h-24 rounded-full p-1" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 50%, #A855F7 100%)' }}>
                  <div className="w-full h-full rounded-full overflow-hidden flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #818CF8 0%, #A78BFA 100%)' }}>
                    {showBrandImg ? (
                      <img
                        src={brandPhotoUrl}
                        alt={brand?.name || "Brand"}
                        className="w-full h-full object-cover"
                        crossOrigin="anonymous"
                        onError={() => setBrandImgFailed(true)}
                      />
                    ) : (
                      <span className="text-white text-3xl font-bold">
                        {(brand?.name || brand?.company_name || "B").charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-[10px] px-3 py-1 rounded-full font-semibold whitespace-nowrap shadow-lg">
                  Brand
                </div>
              </div>

              {/* Connection Icon */}
              <div className="flex flex-col items-center">
                <Handshake className="w-10 h-10 text-yellow-400" />
              </div>

              {/* Star Photo */}
              <div className="relative">
                <div className="w-24 h-24 rounded-full p-1" style={{ background: 'linear-gradient(135deg, #EC4899 0%, #F472B6 50%, #93C5FD 100%)' }}>
                  <div className="w-full h-full rounded-full overflow-hidden flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #F9A8D4 0%, #A5B4FC 100%)' }}>
                    {showStarImg ? (
                      <img
                        src={starPhotoUrl}
                        alt={star?.name || "Star"}
                        className="w-full h-full object-cover"
                        crossOrigin="anonymous"
                        onError={() => setStarImgFailed(true)}
                      />
                    ) : (
                      <span className="text-white text-3xl font-bold">
                        {(star?.name || "S").charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
                {/* Pro Creator Badge */}
                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-[#7B2FBE] text-white text-[10px] px-2 py-1 rounded-full font-semibold whitespace-nowrap flex items-center gap-1 shadow-lg">
                  <BadgeCheck className="w-3 h-3" />
                  Pro Creator
                </div>
              </div>
            </div>

            {/* Star Name & Info */}
            <div className="text-center mb-4">
              <h3 className="text-white font-outfit font-bold text-lg flex items-center justify-center gap-2">
                {star?.name || "Star"}
                <BadgeCheck className="w-5 h-5 text-[#87CEEB]" />
              </h3>
              <p className="text-white/70 text-sm">
                Pro Creator
              </p>
              {brand?.name || brand?.company_name ? (
                <p className="text-white/50 text-xs mt-1">
                  worked with <span className="text-white/80 font-medium">{brand?.name || brand?.company_name}</span>
                </p>
              ) : null}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-center gap-2 pt-4 border-t border-white/10">
              <Star className="w-4 h-4 text-[#FF69B4]" />
              <span className="text-white/60 text-xs font-medium">FOUNDSTAR</span>
              <span className="text-white/40 text-xs">• Where Brands Find Their Star</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2 mt-4">
          <Button
            onClick={downloadCard}
            disabled={downloading}
            className="flex-1 bg-gradient-to-r from-[#FF69B4] to-[#87CEEB] hover:opacity-90 text-white"
            data-testid="download-deal-card"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Download Card
          </Button>
          <Button
            onClick={shareNative}
            variant="outline"
            className="flex-1"
            data-testid="share-deal-native"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
        </div>

        {/* Social Share Buttons */}
        <div className="mt-4">
          <p className="text-sm text-[#64748B] mb-2">Share on:</p>
          <div className="flex flex-wrap gap-2">
            {socials.map((s) => (
              <a
                key={s.key}
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`${s.bg} text-white p-2 rounded-full transition-colors`}
                title={s.label}
              >
                <s.Icon className="w-4 h-4" />
              </a>
            ))}
            <button
              onClick={copyLink}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 p-2 rounded-full transition-colors"
              title="Copy"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
